/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useCallback } from 'react';
import { Box } from '@mui/material';
import { Close } from '@mui/icons-material';
import QSModal from '../../../../../../shared/components/molecules/modal';
import { QSButton, QSIconButton, QSTypography } from '../../../../../../shared/components/atoms';
import ProgressiveImage from '../../../../../../shared/components/atoms/progressive-image';
import TickTokIcon from '../../../../../../shared/icons/TickTokIcon';
import LinkIcon from '../../../../../../shared/icons/icons-revamp/link';
import BroadcastGroupIcon from '../../../../../../shared/icons/icons-revamp/user_group';
import { useCustomNavigate } from '../../../../../../shared/hooks/use-conversation-navigate';
import { ROUTES } from '../../../../../../shared/constants/routes';

import WAGroupImg from '../../../../../../shared/assets/img/ChannelPartner/WAGroup.webp';
import MessageCheckIcon from '../../../../../../shared/icons/icons-revamp/message_check';
import AccountManagerIcon from '../../../../../../shared/icons/AccountManagerIcon';
import AiAgentIcon from '../../../../../../shared/icons/icons-revamp/ai_agent';

type Props = {
  open: boolean;
  onClose: () => void;
};

const FEATURES = [
  {
    id: 1,
    label: 'Generate WhatsApp Group link and send invite your customers',
    icon: <LinkIcon size={20} color="#009DE2" />,
  },
  {
    id: 2,
    label: 'Invite customers on standard WA Number or WA Business App',
    icon: <MessageCheckIcon width={20} height={24} color="#3F906D" />,
  },
  {
    id: 3,
    label: 'Add and manage upto 8 members',
    icon: <AccountManagerIcon size={20} color="#DD661C" />,
  },
  {
    id: 4,
    label: 'Assign group chats to AI agents',
    icon: <AiAgentIcon size={20} color="#8959E5" />,
    comingSoon: true,
  },
];

const WAGroupsBlockerModal = ({ open, onClose }: Props) => {
  const navigate = useCustomNavigate();

  const onClickUpgrade = useCallback(() => {
    navigate({ pathname: `${ROUTES.SETTINGS}${ROUTES.SETTINGS_BILLING}` });
    onClose();
  }, [navigate, onClose]);

  if (!open) {
    return null;
  }

  return (
    <QSModal
      css={css`
        display: flex;
        justify-content: center;
        align-items: center;
      `}
      open={open}
      onClose={onClose}
    >
      <Box
        css={css`
          display: flex;
          flex-direction: column;
          overflow: hidden;
          outline: none;
          position: relative;
          width: 100%;
          max-width: 730px;
        `}
      >
        <QSIconButton
          css={css`
            position: absolute;
            top: 8px;
            right: 8px;
            z-index: 1;
          `}
          sx={{}}
          onClick={onClose}
        >
          <Close />
        </QSIconButton>
        <Box
          css={css`
            padding: 16px;
            border-radius: 8px;
            background: white;
            flex: 1;
            overflow: auto;
            display: flex;
            align-items: center;
            gap: 24px;
            align-self: stretch;
          `}
        >
          <Box
            css={css`
              flex: 1;
              display: flex;
              justify-content: flex-end;
              align-items: center;
              align-self: stretch;
              background-color: #f5f0e6;
              border-radius: 4px;
              width: 320px;
            `}
          >
            <ProgressiveImage imgProps={{ width: '100%', height: '353px' }} src={WAGroupImg} />
          </Box>

          <Box
            css={css`
              flex: 1 0 0;
              display: flex;
              flex-direction: column;
              align-items: flex-start;
              justify-content: space-between;
              height: 354px;
              max-width: 350px;
              align-self: stretch;
            `}
          >
            <Box
              css={css`
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                gap: 16px;
                align-self: stretch;
              `}
            >
              <Box
                css={css`
                  display: flex;
                  flex-direction: column;
                  align-items: flex-start;
                  gap: 8px;
                  flex: 1 0 0;
                `}
              >
                <Box
                  css={css`
                    display: inline-flex;
                    padding: 4px 8px 4px 0px;
                    align-items: center;
                    gap: 5px;
                  `}
                >
                  <BroadcastGroupIcon color="#3F906D" size={20} />
                  <QSTypography
                    css={css`
                      color: #2d2d2d;
                      font-size: 12px;
                      font-weight: 500;
                    `}
                  >
                    WhatsApp Groups on DoubleTick
                  </QSTypography>
                </Box>
                <QSTypography
                  css={css`
                    color: #2d2d2d;
                    font-size: 20px;
                    font-weight: 600;
                    line-height: 140%;
                  `}
                >
                  Create WhatsApp Groups using WhatsApp API Number{' '}
                  <span
                    css={css`
                      color: #7a7a7a;
                    `}
                  >
                    & enable customer to customer chat
                  </span>
                </QSTypography>
              </Box>

              <Box
                css={css`
                  display: flex;
                  flex-direction: column;
                  align-items: flex-start;
                  gap: 12px;
                `}
              >
                {FEATURES.map((feature) => (
                  <Box
                    key={feature.id}
                    css={css`
                      display: flex;
                      width: 100%;
                      align-items: center;
                      gap: 8px;
                    `}
                  >
                    <Box
                      css={css`
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        flex-shrink: 0;
                      `}
                    >
                      {feature.icon}
                    </Box>
                    <QSTypography
                      css={css`
                        color: #2d2d2d;
                        font-size: 12px;
                        font-weight: 500;
                        flex: 1;
                      `}
                    >
                      {feature.label}
                    </QSTypography>
                    {feature.comingSoon && (
                      <Box
                        css={css`
                          display: inline-flex;
                          padding: 2px 8px;
                          align-items: center;
                          border-radius: 6px;
                          background: rgba(30, 41, 59, 0.08);
                          white-space: nowrap;
                        `}
                      >
                        <QSTypography
                          css={css`
                            color: #7a7a7a;
                            font-size: 11px;
                            font-weight: 500;
                          `}
                        >
                          Coming soon
                        </QSTypography>
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            </Box>

            <Box
              css={css`
                width: 100%;
                display: flex;
                justify-content: center;
                align-items: flex-start;
                align-self: stretch;
              `}
            >
              <QSButton
                css={css`
                  flex: 1 0 0;
                  display: flex;
                  height: 40px;
                  padding: 8px 22px;
                  flex-direction: column;
                  justify-content: center;
                  align-items: center;
                  border-radius: 4px;
                  background: #3c8461;
                  box-shadow: none !important;
                  &:hover {
                    box-shadow: none !important;
                  }
                `}
                onClick={onClickUpgrade}
              >
                Upgrade to PRO plan
              </QSButton>
            </Box>
          </Box>
        </Box>
      </Box>
    </QSModal>
  );
};

export default WAGroupsBlockerModal;
